import { useWorkflowStepGeneratedDocument } from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DocViewerComponent,
  Icons,
  LoadingIndicator,
} from '@commenda/kakashi';

type WorkflowGeneratedDocumentModalProps = {
  isOpen: boolean;
  onClose: () => void;
  workflowExecutionId: string;
  stepId: string;
};

/** Previews the document a workflow agent step generated through the shared doc viewer, with a signed download link. */
export const WorkflowGeneratedDocumentModal = ({
  isOpen,
  onClose,
  workflowExecutionId,
  stepId,
}: WorkflowGeneratedDocumentModalProps) => {
  const { data, error, isLoading } = useWorkflowStepGeneratedDocument(
    isOpen ? { workflowExecutionId, stepId } : null
  );

  const onDownload = () => {
    if (data?.signedUrl) window.open(data.signedUrl, '_blank', 'noopener');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="flex flex-col max-w-5xl"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>{data?.fileName ?? 'Generated document'}</DialogTitle>
        </DialogHeader>
        <div className="h-[500px] overflow-y-auto p-3xl">
          {isLoading && (
            <div className="flex items-center justify-center py-6xl">
              <LoadingIndicator />
            </div>
          )}
          {!isLoading && (error || !data) && (
            <div className="text-center py-6xl">
              <Icons.AlertCircle className="size-12 text-text-error mx-auto mb-xl" />
              <p className="txt-medium-medium text-text-primary mb-md">
                Failed to load document
              </p>
              <p className="txt-small-regular text-text-tertiary">
                An error occurred while loading the generated document. Please
                try again.
              </p>
            </div>
          )}
          {!isLoading && !error && data && (
            <DocViewerComponent uri={data.signedUrl} />
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!data?.signedUrl}
            onClick={onDownload}
          >
            <Icons.Download01 className="size-4" /> Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
